/**
 * Backup size policy and storage preflight (D-030, D-031).
 *
 * These thresholds are INITIAL, deliberately conservative values. They exist
 * because a complete backup generally must be materialised as a `Blob` on iOS —
 * there is no File System Access API there, so the artifact is handed to the
 * Share sheet or a download link. Segment size is therefore the memory control.
 *
 * They are explicitly subject to revision after real-device testing on the
 * iOS 17+ baseline, and they live here rather than being scattered through the
 * backup code.
 */

const MB = 1024 * 1024

export interface SizePolicy {
  /** At or below this, a single file is produced. */
  readonly singleFileLimitBytes: number
  /** Above this, the backup is segmented automatically. */
  readonly segmentAboveBytes: number
  /** Maximum size of one segment. */
  readonly segmentTargetBytes: number
  /** Free space required beyond the estimated artifact size. */
  readonly headroomBytes: number
}

export const DEFAULT_SIZE_POLICY: SizePolicy = {
  singleFileLimitBytes: 250 * MB,
  segmentAboveBytes: 500 * MB,
  segmentTargetBytes: 250 * MB,
  headroomBytes: 50 * MB,
}

export type SizeVerdict =
  | { readonly kind: 'single' }
  | { readonly kind: 'singleWithWarning'; readonly message: string }
  | { readonly kind: 'segmented'; readonly segmentCount: number }

export function classifySize(
  estimatedBytes: number,
  policy: SizePolicy = DEFAULT_SIZE_POLICY,
): SizeVerdict {
  if (estimatedBytes <= policy.singleFileLimitBytes) return { kind: 'single' }

  if (estimatedBytes <= policy.segmentAboveBytes) {
    return {
      kind: 'singleWithWarning',
      message:
        'This backup is large. Creating it may be slow, and it may fail on a device that is low on memory.',
    }
  }

  return {
    kind: 'segmented',
    segmentCount: Math.ceil(estimatedBytes / policy.segmentTargetBytes),
  }
}

export type PreflightVerdict =
  | { readonly kind: 'proceed'; readonly verdict: SizeVerdict }
  | { readonly kind: 'refuse'; readonly message: string }

/**
 * Decide whether an operation of `estimatedBytes` should start at all.
 *
 * `availableBytes` of `undefined` means the Storage API could not tell us. That
 * is NOT treated as permission to assume space: the operation proceeds and must
 * handle a real `QuotaExceededError`, which the caller does.
 */
export function preflightSize(
  estimatedBytes: number,
  availableBytes: number | undefined,
  policy: SizePolicy = DEFAULT_SIZE_POLICY,
): PreflightVerdict {
  if (availableBytes !== undefined && availableBytes < estimatedBytes + policy.headroomBytes) {
    return {
      kind: 'refuse',
      message:
        'There is not enough free space on this device to create the backup safely. Free some space, or delete photos you no longer need, and try again.',
    }
  }
  return { kind: 'proceed', verdict: classifySize(estimatedBytes, policy) }
}

/**
 * Space a restore needs.
 *
 * A restore transiently holds THREE things: the current dataset, a full
 * pre-restore recovery snapshot of it (including blob bytes, because a restore
 * replaces blobs), and the incoming payload. The snapshot is never silently
 * skipped to make a restore fit (D-031) — if the space is not there, the
 * restore is refused and the user is directed to export a manual backup first,
 * which moves that copy outside the origin quota.
 */
export function preflightRestore(
  currentDatasetBytes: number,
  incomingPayloadBytes: number,
  availableBytes: number | undefined,
  policy: SizePolicy = DEFAULT_SIZE_POLICY,
): PreflightVerdict {
  const required = currentDatasetBytes + incomingPayloadBytes
  if (availableBytes !== undefined && availableBytes < required + policy.headroomBytes) {
    return {
      kind: 'refuse',
      message:
        'There is not enough free space to restore safely. Project Sola keeps a copy of your current data until you confirm the restore looks right, and will not skip that copy. Save a backup file to your device first, then free some space and try again.',
    }
  }
  return { kind: 'proceed', verdict: { kind: 'single' } }
}
